import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api, { backendUrl } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, TrendingDown, Trash2, Receipt, Loader2, Plus, AlertTriangle, Eye, X, Pencil, Check } from "lucide-react";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TIPI_SPESA = [
  { v: "spesa_viva", l: "Spesa viva", desc: "bolli, marche, spedizioni, traduzioni…" },
  { v: "onorario_terzi", l: "Onorario / fattura a terzi", desc: "avvocato, commercialista, consulente…" },
];
const TONO_STATO = {
  Saldato: "bg-[#D8E6D2] text-[#1B3A22]",
  Parziale: "bg-[#F5EBCF] text-[#8a6d1f]",
  "Non incassato": "bg-[#ECEAE3] text-[#4A4A48]",
  Eccedenza: "bg-[#FDF6F1] text-[#9A3B13]",
};
const etich = "text-[10px] font-medium uppercase tracking-[0.1em] text-[#8a93a6]";
const campo = "mt-1 w-full px-2.5 py-2 text-sm border border-[#1A2B4A]/15 rounded-lg bg-white focus:outline-none focus:border-[#C9A84C]";
const oggiISO = () => new Date().toISOString().slice(0, 10);
const dataIt = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const viewUrl = (docId) => `${backendUrl}/api/documenti/${docId}/download?inline=1&token=${localStorage.getItem("rb_token")}`;

function Cifra({ etichetta, valore, spiega, tono, testid, fondo }) {
  return (
    <div data-testid={testid} className="cifra" style={fondo ? { background: "#F7F6F2" } : undefined}>
      <span className="eti">{etichetta}</span>
      <b className={tono}>{eur(valore)}</b>
      <small>{spiega}</small>
    </div>
  );
}

export default function PraticaDenaro({ pratica, onChanged, children, senzaCornice, azione, onAzioneFatta }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const vedeSoldi = can("vedi_dati_economici");
  const box = useRef(null);
  const [apri, setApri] = useState(null); // 'incasso' | 'spesa'
  const [busy, setBusy] = useState(false);
  const [incasso, setIncasso] = useState({ importo: "", data: oggiISO(), causale: "", mezzo: "" });
  const [eccedenza, setEccedenza] = useState(null);
  const [spesa, setSpesa] = useState({ tipo: "spesa_viva", descrizione: "", fornitore: "", importo: "", data: "", pagato: false });
  const [ric, setRic] = useState(null); // { incasso_id, data, causale }
  const [modConcordato, setModConcordato] = useState(null);

  const salvaConcordato = async () => {
    try {
      await api.put(`/pratiche/${pratica.id}/dettagli`, { importo_totale: Number(modConcordato) || 0 });
      setModConcordato(null);
      toast.success("Importo concordato aggiornato");
      refresh();
    } catch { toast.error("Aggiornamento non riuscito"); }
  };

  const { data: d, isLoading } = useQuery({
    queryKey: ["denaro", pratica.id],
    queryFn: async () => (await api.get(`/pratiche/${pratica.id}/denaro`)).data,
    enabled: vedeSoldi,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["denaro", pratica.id] }); onChanged?.(); };

  // Azione chiesta da un'altra pagina (per esempio dalla scheda cliente)
  useEffect(() => {
    if (!azione) return;
    if (azione === "ricevuta") {
      if (!d) return; // attende i movimenti per scegliere l'incasso da documentare
      apriRicevuta();
    } else {
      setApri(azione);
    }
    box.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    onAzioneFatta?.();
  }, [azione, d]); // eslint-disable-line react-hooks/exhaustive-deps

  const pct = useMemo(() => {
    if (!d || !d.concordato) return 0;
    return Math.min(100, Math.max(0, (d.incassato / d.concordato) * 100));
  }, [d]);

  const salvaIncasso = async (conferma = false) => {
    if (!(Number(incasso.importo) > 0)) return toast.error("L'importo dell'incasso deve essere maggiore di zero");
    setBusy(true);
    try {
      await api.post(`/pratiche/${pratica.id}/pagamenti`, {
        importo: Number(incasso.importo), data: incasso.data || undefined,
        causale: incasso.causale, mezzo: incasso.mezzo, conferma_eccedenza: conferma,
      });
      setIncasso({ importo: "", data: oggiISO(), causale: "", mezzo: "" });
      setApri(null); setEccedenza(null);
      toast.success("Incasso registrato");
      refresh();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.errore === "eccedenza") setEccedenza(det);
      else toast.error(typeof det === "string" ? det : "Incasso non registrato");
    } finally { setBusy(false); }
  };

  const eliminaIncasso = async (m) => {
    if (!window.confirm(`Eliminare l'incasso di ${eur(m.importo)}?${m.documento ? " La ricevuta collegata risulterà annullata." : ""}`)) return;
    try { await api.delete(`/pratiche/${pratica.id}/pagamenti/${m.id}`); toast.success("Incasso rimosso"); refresh(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const salvaSpesa = async () => {
    if (!spesa.descrizione.trim() || !(Number(spesa.importo) > 0)) return toast.error("Inserisci descrizione e importo della spesa");
    setBusy(true);
    try {
      await api.post(`/pratiche/${pratica.id}/costi`, {
        tipo: spesa.tipo, descrizione: spesa.descrizione.trim(), fornitore: spesa.fornitore.trim(),
        importo: Number(spesa.importo), data: spesa.data || null, pagato: spesa.pagato,
      });
      setSpesa({ tipo: spesa.tipo, descrizione: "", fornitore: "", importo: "", data: "", pagato: false });
      setApri(null);
      toast.success("Spesa registrata");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Spesa non registrata"); }
    finally { setBusy(false); }
  };

  const eliminaSpesa = async (m) => {
    try { await api.delete(`/pratiche/${pratica.id}/costi/${m.id}`); toast.success("Spesa rimossa"); refresh(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const apriRicevuta = () => {
    const liberi = (d?.incassi || []).filter((x) => !x.documento);
    if (!liberi.length) {
      return toast.error("Registra prima l'incasso: la ricevuta documenta un incasso già registrato.");
    }
    setRic({ incasso_id: liberi[0].id, data: oggiISO(), causale: "" });
  };

  const emettiRicevuta = async () => {
    if (!ric?.incasso_id) return toast.error("Scegli l'incasso da documentare");
    setBusy(true);
    try {
      const { data: f } = await api.post(`/pratiche/${pratica.id}/ricevuta-pagamento`, {
        incasso_id: ric.incasso_id, data: ric.data, causale: ric.causale,
      });
      toast.success(`${f.etichetta} n. ${f.numero_completo} emessa sull'incasso di ${eur(f.importo)}`);
      setRic(null);
      qc.invalidateQueries({ queryKey: ["documenti-contabili", pratica.id] });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Documento non generato"); }
    finally { setBusy(false); }
  };

  if (!vedeSoldi) return null;

  const documentoDi = (m) => (m.genere !== "incasso" ? null : m.documento ? (
    <a data-testid={`movimento-ricevuta-${m.id}`}
      href={m.documento.documento_id ? viewUrl(m.documento.documento_id) : m.documento.drive_link}
      target="_blank" rel="noreferrer" className="text-[13px] font-semibold no-underline">
      {m.documento.etichetta} {m.documento.numero_completo}
    </a>
  ) : (
    <button data-testid={`emetti-ricevuta-${m.id}`}
      onClick={() => setRic({ incasso_id: m.id, data: oggiISO(), causale: m.causale === "Incasso" ? "" : m.causale })}
      className="text-[13px] font-semibold text-[#8A6E2F] hover:text-[#6B5523]">
      Emetti ricevuta
    </button>
  ));


  return (
    <div ref={box} data-testid="pratica-denaro"
      className={senzaCornice ? "" : "bg-white border border-[#1A2B4A]/12 rounded-lg p-4 shadow-sm"}>
      {d && (
        <div className="flex items-center justify-end mb-2.5">
          <span data-testid="denaro-stato"
            className={`text-[13px] font-bold rounded-[3px] px-3 py-1.5 ${TONO_STATO[d.stato]}`}>
            {d.stato}
          </span>
        </div>
      )}

      {isLoading || !d ? (
        <div className="flex items-center gap-2 text-sm text-[#4a5568]"><Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" /> Carico i conti…</div>
      ) : (<>
        <div className="cifre">
          <div className="cifra">
            <span className="eti flex items-center gap-1.5">
              Valore pratica
              <button data-testid="denaro-edit-concordato" onClick={() => setModConcordato(modConcordato === null ? String(d.concordato || "") : null)}
                className="text-[#5A5A5A] hover:text-[#A8863F]"><Pencil className="w-3 h-3" /></button>
            </span>
            {modConcordato === null ? (
              <b data-testid="denaro-concordato" className="text-[#1B2A4B]">{eur(d.concordato)}</b>
            ) : (
              <span className="flex gap-1 items-center">
                <input data-testid="denaro-concordato-input" type="number" autoFocus value={modConcordato}
                  onChange={(e) => setModConcordato(e.target.value)}
                  className="w-full px-2 py-1 border border-[#A8863F] rounded-[4px] text-[15px] focus:outline-none" />
                <button data-testid="denaro-concordato-save" onClick={salvaConcordato} className="text-[#2E5E3A]"><Check className="w-4 h-4" /></button>
              </span>
            )}
            <small>dal contratto</small>
          </div>
          <Cifra testid="denaro-incassato" etichetta="Incassato" valore={d.incassato} tono="text-[#2E5E3A]"
            spiega={d.n_incassi === 0 ? "nessun incasso" : `${d.n_incassi} incass${d.n_incassi === 1 ? "o" : "i"} registrat${d.n_incassi === 1 ? "o" : "i"}`} />
          <Cifra testid="denaro-da-incassare" etichetta="Da incassare" valore={d.da_incassare} tono="text-[#1A1A1A]" fondo
            spiega={d.da_incassare <= 0 ? "niente in sospeso" : "valore meno incassato"} />
          <Cifra testid="denaro-spese" etichetta="Spese" valore={d.costi} tono="text-[#1A1A1A]"
            spiega={d.costi > 0 ? `resta allo Studio ${eur(d.resta_allo_studio)}` : "nessuna spesa registrata"} />
        </div>

        <div className="mt-[18px]">
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="apri-incasso" onClick={() => setApri(apri === "incasso" ? null : "incasso")}
              className="btn btn-p flex-[1_1_220px]">Registra incasso</button>
            <button data-testid="apri-spesa-sposta" style={{ display: "none" }} />
            <button data-testid="apri-spesa" onClick={() => setApri(apri === "spesa" ? null : "spesa")}
              className="btn btn-o flex-[1_1_220px]">Registra spesa</button>
            <button data-testid="apri-ricevuta" onClick={apriRicevuta}
              className="btn btn-g flex-[1_1_220px]">Emetti ricevuta / fattura</button>
          </div>

          {apri === "incasso" && (
            <div data-testid="form-incasso" className="mt-3 rounded-xl border border-[#1F5C42]/20 bg-[#f6faf7] p-3 grid sm:grid-cols-2 gap-2">
              <label className="block">
                <span className={etich}>Quanto hai incassato</span>
                <input data-testid="pay-importo" type="number" placeholder="0,00 €" value={incasso.importo}
                  onChange={(e) => { setIncasso({ ...incasso, importo: e.target.value }); setEccedenza(null); }} className={campo} />
              </label>
              <label className="block">
                <span className={etich}>Quando</span>
                <input data-testid="pay-data" type="date" max={oggiISO()} value={incasso.data}
                  onChange={(e) => setIncasso({ ...incasso, data: e.target.value })} className={campo} />
              </label>
              <label className="block">
                <span className={etich}>Causale</span>
                <input data-testid="pay-causale" placeholder="es. primo acconto" value={incasso.causale}
                  onChange={(e) => setIncasso({ ...incasso, causale: e.target.value })} className={campo} />
              </label>
              <label className="block">
                <span className={etich}>Mezzo di pagamento</span>
                <input data-testid="pay-mezzo" placeholder="contanti, bonifico, POS…" value={incasso.mezzo}
                  onChange={(e) => setIncasso({ ...incasso, mezzo: e.target.value })} className={campo} />
              </label>
              {eccedenza && (
                <div data-testid="avviso-eccedenza" className="sm:col-span-2 rounded-md border border-[#C9A84C]/60 bg-[#FDFAF1] px-3 py-2 text-[12px] text-[#7a5c12]">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  {eccedenza.messaggio} È un acconto in più o è un errore?
                </div>
              )}
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <button onClick={() => { setApri(null); setEccedenza(null); }} className="text-[12px] text-[#8a93a6] hover:text-[#1A2B4A] px-2 py-2">Annulla</button>
                <button data-testid="pay-add" onClick={() => salvaIncasso(!!eccedenza)} disabled={busy}
                  className="btn btn-p btn-piccolo">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {eccedenza ? "Confermo, registra comunque" : "Registra incasso"}
                </button>
              </div>
            </div>
          )}

          {apri === "spesa" && (
            <div data-testid="form-spesa" className="mt-3 rounded-xl border border-[#1A2B4A]/12 bg-[#fbfaf7] p-3">
              <div className="flex gap-2 mb-3">
                {TIPI_SPESA.map((t) => (
                  <button key={t.v} data-testid={`spesa-tipo-${t.v}`} onClick={() => setSpesa({ ...spesa, tipo: t.v })}
                    className={`flex-1 text-left px-3 py-2 rounded-lg border text-[12px] transition-colors
                      ${spesa.tipo === t.v ? "border-[#C9A84C] bg-white text-[#1A2B4A]" : "border-[#1A2B4A]/12 text-[#4a5568] hover:border-[#C9A84C]/60"}`}>
                    <span className="block font-semibold">{t.l}</span>
                    <span className="block text-[10px] text-[#8a93a6] leading-tight">{t.desc}</span>
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="sm:col-span-2 block">
                  <span className={etich}>Di che spesa si tratta</span>
                  <input data-testid="costo-descrizione" placeholder="es. marca da bollo, parere legale" value={spesa.descrizione}
                    onChange={(e) => setSpesa({ ...spesa, descrizione: e.target.value })} className={campo} />
                </label>
                <label className="block">
                  <span className={etich}>Importo</span>
                  <input data-testid="costo-importo" type="number" min="0" placeholder="0,00 €" value={spesa.importo}
                    onChange={(e) => setSpesa({ ...spesa, importo: e.target.value })} className={campo} />
                </label>
                <label className="block">
                  <span className={etich}>Data</span>
                  <input data-testid="costo-data" type="date" value={spesa.data}
                    onChange={(e) => setSpesa({ ...spesa, data: e.target.value })} className={campo} />
                </label>
                <label className="block">
                  <span className={etich}>A chi (facoltativo)</span>
                  <input data-testid="costo-fornitore" placeholder="fornitore, collega, ente" value={spesa.fornitore}
                    onChange={(e) => setSpesa({ ...spesa, fornitore: e.target.value })} className={campo} />
                </label>
                <div className="flex items-end justify-between gap-2">
                  <label className="inline-flex items-center gap-1.5 text-[12px] text-[#4a5568] cursor-pointer select-none pb-2">
                    <input data-testid="costo-pagato" type="checkbox" checked={spesa.pagato}
                      onChange={(e) => setSpesa({ ...spesa, pagato: e.target.checked })} className="w-4 h-4 accent-[#C9A84C]" />
                    l'ho già pagata
                  </label>
                  <button data-testid="costo-add" onClick={salvaSpesa} disabled={busy}
                    className="btn btn-p btn-piccolo">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Registra
                  </button>
                </div>
              </div>
            </div>
          )}

          {d.movimenti.length === 0 ? (
            <div className="sub mt-[18px]"><div className="subt"><span>Movimenti</span></div>
              <div className="riga text-[#5A5A5A]" data-testid="movimenti-vuoti">Nessun movimento su questa pratica.</div></div>
          ) : (
            <div className="sub mt-[18px]">
            <div className="subt"><span>Movimenti</span></div>
            <div className="overflow-x-auto">
            <table className="w-full text-[14px]" data-testid="movimenti-tabella">
              <tbody>
                {d.movimenti.map((m) => (
                  <tr key={m.id} data-testid={`movimento-${m.id}`}
                    className="group align-middle border-b border-[#F0EEE8] last:border-0"
                    style={m.genere === "spesa" ? { background: "#F7F6F2" } : undefined}>
                    <td className="px-3.5 py-2.5 text-[14px] text-[#4A4A48] whitespace-nowrap w-[70px] sm:w-[84px] align-top sm:align-middle">{dataIt(m.data)}</td>
                    <td className="py-2.5 pr-2 min-w-0">
                      <div className="text-[#1A1A1A] truncate">{m.genere === "incasso" ? "Incasso" : "Spesa"} · {m.causale}</div>
                      <div className="text-[13px] text-[#5A5A5A] truncate">
                        {m.genere === "incasso" ? (m.mezzo || "incasso") : (m.tipo_costo === "onorario_terzi" ? "onorario a terzi" : "spesa viva")}
                        {m.genere === "spesa" ? (m.pagato ? " · pagata" : " · da pagare") : ""}
                        {m.genere === "spesa" && m.mezzo ? ` · ${m.mezzo}` : ""}
                      </div>
                      <div className="sm:hidden mt-1">{documentoDi(m)}</div>
                    </td>
                    <td className={`py-2.5 text-right font-bold whitespace-nowrap pr-3.5 w-[120px] ${m.importo < 0 ? "text-[#8A2E2E]" : "text-[#2E5E3A]"}`}>
                      {m.importo < 0 ? "− " : "+ "}{eur(Math.abs(m.importo))}
                    </td>
                    <td className="py-2.5 pr-3.5 align-top sm:align-middle">
                      <div className="flex items-center gap-2">
                        <span className="hidden sm:inline">{documentoDi(m)}</span>
                        <button data-testid={`movimento-remove-${m.id}`}
                          onClick={() => (m.genere === "incasso" ? eliminaIncasso(m) : eliminaSpesa(m))}
                          className="ml-auto text-[#9b2c2c]/60 hover:text-[#9b2c2c] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="nota-tenue">
              Importi che il cliente paga, imposte comprese. Un movimento cancellato sparisce da qui e resta solo nello storico.
            </div>
            </div>
          )}

          {d.costi > 0 && (
            <p data-testid="denaro-costi" className="mt-3 text-[13px] text-[#4A4A48]">
              Costi di terzi e spese: <b className="text-[#8A2E2E]">{eur(d.costi)}</b> · resta allo Studio:{" "}
              <b className="text-[#2E5E3A]">{eur(d.resta_allo_studio)}</b>
            </p>
          )}
        </div>
      </>)}

      {ric && (
        <div className="fixed inset-0 z-[70] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="dialogo-ricevuta">
          <div className="bg-white rounded-[1.5rem] w-full max-w-lg p-4 border-t-2 border-[#C9A84C] shadow-[0_20px_40px_rgba(0,0,0,0.12)] max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-2xl font-semibold tracking-tight text-[#1A2B4A]">Emetti ricevuta</h3>
              <button data-testid="ricevuta-close" onClick={() => setRic(null)} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[12px] text-[#4a5568] mb-3">
              La ricevuta documenta un incasso già registrato: non aggiunge denaro alla pratica.
            </p>
            <label className="block">
              <span className={etich}>Per quale incasso</span>
              <select data-testid="ricevuta-incasso" value={ric.incasso_id}
                onChange={(e) => setRic({ ...ric, incasso_id: e.target.value })} className={campo}>
                {(d?.incassi || []).map((x) => (
                  <option key={x.id} value={x.id} disabled={!!x.documento}>
                    {dataIt(x.data)} · {eur(x.importo)} · {x.causale}
                    {x.documento ? ` — già documentato (n. ${x.documento.numero_completo})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block">
                <span className={etich}>Data della ricevuta</span>
                <input data-testid="ricevuta-data" type="date" max={oggiISO()} value={ric.data}
                  onChange={(e) => setRic({ ...ric, data: e.target.value })} className={campo} />
              </label>
              <label className="block">
                <span className={etich}>Causale</span>
                <input data-testid="ricevuta-causale" placeholder={`Assistenza pratica ${pratica.tipo_pratica_nome || ""}`}
                  value={ric.causale} onChange={(e) => setRic({ ...ric, causale: e.target.value })} className={campo} />
              </label>
            </div>
            <p className="text-[11px] text-[#8a93a6] mt-3">
              Numero progressivo assegnato dal gestionale. La data non può essere nel futuro né precedere l'incasso.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setRic(null)} disabled={busy}
                className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-md hover:bg-[#f9f8f5] transition-colors">Annulla</button>
              <button data-testid="ricevuta-conferma" onClick={emettiRicevuta} disabled={busy}
                className="btn btn-g">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Emetto…" : "Emetti"}
              </button>
            </div>
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
